import { Injectable } from "@angular/core";
import {
  AuthClient,
  LoginModel,
  UserClient,
  RegistrationModel,
  ValidateModel,
  ResetPasswordModel,
  NewPasswordModel,
  ValidatePhoneModel, PhoneValidationClient, VerifyPhoneModel, ValidateResponseModel
} from "../api/WickedApiClient";
import { setAuthToken, isAuthenticated, getUserTokenFieldSchema, isTokenExpired, getAuthToken } from "../api/AuthHeaders";
import { MainService } from "./service.service";
import { LoggerMethod, LoggerLevel } from "../Logger/Logger";
import { isNumber } from "util";
import { JwtHelper } from "../Helpers/JwtHelper";
import { Router } from "@angular/router";
import { AdvertisementService } from "./advertisement.service";
import { CookieService } from "ngx-cookie-service";

//@LogClass()
@Injectable({
  providedIn: "root"
})
export class UserService {
  services: MainService = null;

  constructor(private authClient: AuthClient, private userClient: UserClient, private phoneValidationClient: PhoneValidationClient, public route: Router, private advertisementService: AdvertisementService, private cookieService: CookieService) {
  }

  @LoggerMethod()
  login(email: string, password: string, rememberme: boolean = true, returnUrl: string = ""): Promise<string> {
    return new Promise<string>((rs, rj) => {
      const loginModel = new LoginModel();

      loginModel.init({
        userName: email,
        password: password
      });
      if (rememberme) {
        localStorage.setItem("email", email);
      } else {
        localStorage.removeItem("email");
      }
      this.services.progress = true;
      this.authClient.login(loginModel).subscribe(result => {

        if (result.phoneVerificationRequired && !result.isDemo) {
          this.logout();
          // rj("You must finish phone number verification");
          const userDataObject = btoa(JSON.stringify({ userId: result.userId.toString(), userPhone: result.userPhone }));
          this.route.navigate(["/validate-phone/" + userDataObject]);
        }
        else {
          if (result.error && result.error != "") {
            rj(result.error);
          } else {
            setAuthToken(result.token);
            rs(returnUrl);
          }
        }


        this.services.progress = false;
      });
    });
  }

  @LoggerMethod()
  logout() {
    this.advertisementService.clearSearchData();
    this.services.showStartVideo = false;
    this.clearSearchData();
    setAuthToken(undefined);
  }

  async deleteAccount() {
    return await this.userClient.deleteAccount().toPromise()
  }

  clearSearchData() {
    this.cookieService.delete("search-data");
  }

  isAdmin() {
    return getUserTokenFieldSchema("role").indexOf("admin") > -1;
  }

  isDemo() {
    return getUserTokenFieldSchema("role").indexOf("demo") > -1;
  }

  isPremium() {
    const currentRole = getUserTokenFieldSchema("role");
    return currentRole.indexOf("premium") > -1;
  }

  @LoggerMethod()
  register(email: string, phone: string, password: string, passwordConfirm: string, returnUrl: string = ""): Promise<string> {
    return new Promise<string>((rs, rj) => {
      var model = new RegistrationModel();
      model.init({
        email: email,
        password: password,
        passwordConfirmation: passwordConfirm,
        phone: phone
      });
      this.services.progress = true;
      this.userClient.register(model).subscribe(result => {
        if (result.error && result.error != "") {
          rj(result.error);
        } else {
          rs(returnUrl);
        }
        this.services.progress = false;
      });
    });
  }

  @LoggerMethod()
  resetPassword(email: string, returnUrl: string = ""): Promise<string> {
    return new Promise<string>((rs, rj) => {
      var model = new ResetPasswordModel();
      model.init({
        email: email
      });
      this.services.progress = true;
      this.userClient.resetPassword(model).subscribe(result => {
        if (result.error && result.error != "") {
          rj(result.error);
        } else {
          rs(returnUrl);
        }
        this.services.progress = false;
      });
    });
  }

  @LoggerMethod()
  getEmailFromCode(code: string): Promise<string> {
    return new Promise<string>((rs, rj) => {
      this.services.progress = true;
      this.userClient.getEmailFromCode(code).subscribe(result => {
        if (result.error && result.error != "") {
          rj(result.error);
        } else {
          rs(result.email);
        }
        this.services.progress = false;
      });
    });
  }

  @LoggerMethod()
  newPassword(email: string, code: string, password: string, passwordConfirm: string, returnUrl: string = ""): Promise<string> {
    return new Promise<string>((rs, rj) => {
      const model = new NewPasswordModel();

      model.init({
        email: email,
        code: code,
        password: password,
        passwordConfirm: passwordConfirm
      });
      this.services.progress = true;
      this.userClient.newPassword(model).subscribe(result => {
        if (result.error && result.error != "") {
          rj(result.error);
        } else {
          rs(returnUrl);
        }
        this.services.progress = false;
      });
    });
  }

  sendPhoneValidationMessage(userId): Promise<string> {

    return new Promise<string>((success, reject) => {

      const model = new VerifyPhoneModel();
      model.init(
        {
          userId: userId
        }
      );

      this.phoneValidationClient.sendPhoneValidationSms(model).subscribe(result => {
        if (result.error && result.error != "") {
          reject(result.error)
        } else {
          success("")
        }
      })
    });
  }

  validatePhone(userId: Number, code: String): Promise<string | void> {
    return new Promise<string | void>((success, reject) => {
      const model = new ValidatePhoneModel();
      model.init({
        code: code,
        userId: userId
      }
      )
      this.userClient.validatePhone(model).subscribe(result => {
        if (result.error && result.error != "") {
          reject(result.error)
        } else success()
      });
    });
  }

  @LoggerMethod()
  validateEmail(code: string, email: string, returnUrl: string = ""): Promise<ValidateResponseModel> {
    return new Promise<ValidateResponseModel>((rs, rj) => {
      const model = new ValidateModel();
      model.init({
        code: code,
        type: "email",
        value: email
      });
      this.services.progress = true;
      this.userClient.validateEmail(model).subscribe(result => {
        if (result.error && result.error != "") {
          rj(result);
        } else {
          rs(result);
        }
        this.services.progress = false;
      });
    });
  }

  isTokenExpired() {
    return isTokenExpired();
  }

  isTokenExist() {
    if (getAuthToken()) {
      return true;
    }
    return false;

  }

  //@LoggerMethod()
  isAuthenticated() {
    return isAuthenticated();
  }

  //@LoggerMethod()
  getUserEmail() {
    return getUserTokenFieldSchema("emailaddress");
  }

  // @LoggerMethod()
  getUserPhone() {
    return getUserTokenFieldSchema("mobilephone");
  }

  // @LoggerMethod()
  getUserRole() {
    return getUserTokenFieldSchema("role");
  }

  getUserId() {
    var tmp = Number(getUserTokenFieldSchema("nameidentifier"));

    return isNumber(Number(tmp)) ? Number(tmp) : null;
  }
}
